import { getCurrentOperatorName } from '../../utils/currentOperator';
import React from 'react';
import axios from 'axios';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  EconomicsPreview,
  ScopePill,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  TradeEconomicsGrid,
} from './shared/StructuredCreditTicketLayout';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { BadgeDollarSign, CalendarDays, Home, Landmark, ListChecks, TrendingDown, Users } from 'lucide-react';

import { DateField } from '../shared/DateField';

const fmtAmount = (value: string) => {
  const parsed = parseAmountShorthand(value);
  return parsed > 0 ? formatAmountWithCommas(parsed) : value;
};
const num = (value: any, fallback = 0) => {
  const n = value === '' || value == null ? fallback : parseAmountShorthand(String(value));
  return Number.isFinite(n) ? n : fallback;
};

/**
 * RML — residential whole-loan pool acquisition (Phase 5A). Originate-once,
 * position-anchored (settle = trade date), no reference split: the pool is defined
 * inline. prepare() creates dim_rml_pool + the SMF; the trade posts BUY via
 * RMLPostingSpi. Books like a bond: quantity = UPB, price = % of par.
 */
const RMLTicketScreen: React.FC = () => {
  const initialFormData = {
    transactionType: 'BUY' as const,
    assetManager: '', portfolioManager: '',
    assetClass: 'RESIDENTIAL_WHOLE_LOAN', assetSubClass: 'RML Pool',
    portfolio: '', tradeDate: getTodayDate(), settleDate: getTodayDate(),
    submittedBy: getCurrentOperatorName(), currency: 'USD', executedBy: '', notes: '',
    // Pool (reference, inline — originate-once)
    poolName: '', poolType: 'NON_QM', agency: 'NON_AGENCY', lienPosition: 'FIRST',
    originatorName: '', sellerName: '', servicerName: '', productType: 'FRM_30',
    waMaturityDate: '',
    numLoans: '', waFico: '', waLtvPct: '', waDtiPct: '', wacPct: '',
    cpr3mPct: '', cdrPct: '', severityPct: '', delinquency90PlusPct: '',
    // Investment
    poolBalance: '', price: '99.0',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    const isCreate = action === 'draft' || action === 'submitForPreClear';
    if (isCreate) {
      if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
      if (num(formData.poolBalance) <= 0) errors.poolBalance = 'Pool UPB must be > 0';
      if (num(formData.price) <= 0) errors.price = 'Price must be > 0';
      if (!formData.tradeDate) errors.tradeDate = 'Trade date is required';
    }
    if (action === 'draft' && !formData.poolName?.trim()) errors.poolName = 'Pool name is required';
    if (action === 'execute' && !formData.executedBy?.trim()) errors.executedBy = 'Executed By is required';
    return errors;
  };

  const loadTransactionData = async (transaction: any) => {
    const str = (v: any) => (v == null ? '' : v.toString());
    const base: any = {
      ...initialFormData,
      transactionType: transaction.transactionType || 'BUY',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      cusip: transaction.cusip || '',
      poolBalance: str(transaction.amount != null ? transaction.amount : transaction.quantity),
      price: transaction.price != null ? transaction.price.toString() : '',
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
    };
    if (transaction.securityId) {
      try {
        const { data: d } = await axios.get(`/api/credit/rml/securities/${transaction.securityId}/deal-detail`);
        if (d?.found) {
          if (d.poolName) base.poolName = d.poolName;
          if (d.poolType) base.poolType = d.poolType;
          if (d.agency) base.agency = d.agency;
          if (d.lienPosition) base.lienPosition = d.lienPosition;
          if (d.originatorName) base.originatorName = d.originatorName;
          if (d.sellerName) base.sellerName = d.sellerName;
          if (d.servicerName) base.servicerName = d.servicerName;
          if (d.productType) base.productType = d.productType;
          if (d.waMaturityDate) base.waMaturityDate = d.waMaturityDate;
          if (d.numLoans != null) base.numLoans = str(d.numLoans);
          if (d.waFico != null) base.waFico = str(d.waFico);
          if (d.waLtvPct != null) base.waLtvPct = str(d.waLtvPct);
          if (d.waDtiPct != null) base.waDtiPct = str(d.waDtiPct);
          if (d.wacPct != null) base.wacPct = str(d.wacPct);
          if (d.cpr3mPct != null) base.cpr3mPct = str(d.cpr3mPct);
          if (d.cdrPct != null) base.cdrPct = str(d.cdrPct);
          if (d.severityPct != null) base.severityPct = str(d.severityPct);
          if (d.delinquency90PlusPct != null) base.delinquency90PlusPct = str(d.delinquency90PlusPct);
        }
      } catch { /* best-effort */ }
    }
    return base;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);

    const upb = num(formData.poolBalance);
    const price = num(formData.price, 100);

    const preparePayload = {
      portfolioId: portfolio.id,
      poolName: formData.poolName.trim(),
      poolType: formData.poolType,
      agency: formData.agency,
      lienPosition: formData.lienPosition,
      originatorName: formData.originatorName?.trim() || null,
      sellerName: formData.sellerName?.trim() || null,
      servicerName: formData.servicerName?.trim() || null,
      productType: formData.productType,
      currency: formData.currency || 'USD',
      acquisitionDate: formData.tradeDate,
      waMaturityDate: formData.waMaturityDate || null,
      originalPoolBalance: upb,
      purchasePricePct: price,
      numLoans: formData.numLoans ? parseInt(formData.numLoans, 10) : null,
      wacPct: formData.wacPct ? num(formData.wacPct) : null,
      waFico: formData.waFico ? parseInt(formData.waFico, 10) : null,
      waLtvPct: formData.waLtvPct ? num(formData.waLtvPct) : null,
      waDtiPct: formData.waDtiPct ? num(formData.waDtiPct) : null,
      cpr3mPct: formData.cpr3mPct ? num(formData.cpr3mPct) : null,
      cdrPct: formData.cdrPct ? num(formData.cdrPct) : null,
      severityPct: formData.severityPct ? num(formData.severityPct) : null,
      delinquency90PlusPct: formData.delinquency90PlusPct ? num(formData.delinquency90PlusPct) : null,
    };
    const prepareRes = await axios.post('/api/credit/rml/prepare', preparePayload);
    const { securityId, cusip, sourceRef } = prepareRes.data;

    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((a: any) => a.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((p: any) => p.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }

    // Whole-loan BUY: amount carries the UPB (face); engine derives cash = UPB × price / 100.
    // Position-anchored → settle = trade date.
    return {
      transactionType: 'BUY',
      securityId,
      portfolioId: portfolio.id,
      assetManagerId,
      portfolioManagerId,
      quantity: upb,
      price,
      amount: upb,
      tradeDate: formData.tradeDate,
      settleDate: formData.tradeDate,
      notes: formData.notes || `RML whole-loan pool acquisition: ${formData.poolName}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'RML',
      eventType: 'ACQUIRE',
      sourceRef,
      currency: formData.currency || 'USD',
      cusip,
    };
  };

  return (
    <BaseTicketScreen
      title="RML — Residential Whole-Loan Pool"
      assetClass="RESIDENTIAL_WHOLE_LOAN"
      assetSubClass="RML Pool"
      hidePortfolioField
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange }) => {
        const poolBalance = num(formData.poolBalance);
        const price = num(formData.price, 100);
        const consideration = poolBalance * price / 100;

        return (
          <StructuredWorkspace>
            <StructuredHeroPanel
              toneName="rml"
              icon={Home}
              eyebrow="Real Estate"
              title="Residential Mortgage Loan Pool"
              description="Residential whole-loan pool acquisition with pool identity, collateral quality, prepayment, credit, and UPB economics together."
              badges={['RML', 'UPB x price', 'Position anchored']}
              signals={[
                { label: 'Pool', value: formData.poolName || 'No pool', accent: !!formData.poolName },
                { label: 'Pool Type', value: formData.poolType || '-' },
                { label: 'Lien', value: formData.lienPosition || '-' },
                { label: 'Agency', value: formData.agency || '-' },
              ]}
            />

            <TradeEconomicsGrid>
              <StructuredBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow $tone="rml"><CalendarDays size={14} /> Trade Setup</BandEyebrow>
                    <BandTitle>Portfolio, Dates, And Pool Price</BandTitle>
                    <BandSubline>Originate-once residential whole-loan pool with settle date anchored to trade date.</BandSubline>
                  </BandTitleGroup>
                  <ScopePill $tone="rml">BUY</ScopePill>
                </BandHeader>

                <FormRow>
                  <FormGroup>
                    <Label>Portfolio*</Label>
                    <Select value={formData.portfolio} onChange={(e) => handleInputChange('portfolio', e.target.value)} className={errors.portfolio ? 'error' : ''} disabled={isFieldDisabled('portfolio')}>
                      <option value="">Select Portfolio</option>
                      {dimensionData.portfolios.map((p: any) => (<option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label><Landmark size={13} /> Asset Manager</Label>
                    <Select value={formData.assetManager} onChange={(e) => handleInputChange('assetManager', e.target.value)} disabled={isFieldDisabled('assetManager')}>
                      <option value="">Select</option>
                      {dimensionData.assetManagers.map((am: any) => (<option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label><Users size={13} /> Portfolio Manager</Label>
                    <Select value={formData.portfolioManager} onChange={(e) => handleInputChange('portfolioManager', e.target.value)} disabled={isFieldDisabled('portfolioManager')}>
                      <option value="">Select</option>
                      {dimensionData.portfolioManagers.map((pm: any) => (<option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Trade Date*</Label>
                    {/* Settle is anchored to trade (buildTransactionData sends settle = trade), so the read-only settle field follows it. */}
                    <DateField value={formData.tradeDate ?? ''} className={errors.tradeDate ? 'error' : ''} disabled={isFieldDisabled('tradeDate')} calendars={calendarsFor('RML', formData.currency)} onChange={iso => { handleInputChange('tradeDate', iso); handleInputChange('settleDate', iso); }} />
                    {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Settle Date</Label>
                    <DateField value={formData.settleDate ?? ''} disabled calendars={settlementCalendars({ ticketType: 'RML', currency: formData.currency })} onChange={iso => handleInputChange('settleDate', iso)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                    <Select value={formData.executedBy} onChange={(e) => handleInputChange('executedBy', e.target.value)} disabled={isFieldDisabled('executedBy')} className={errors.executedBy ? 'error' : ''}>
                      <option value="">Select</option>
                      {dimensionData.users.map((u: any) => (<option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>))}
                    </Select>
                    {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label><BadgeDollarSign size={13} /> Pool UPB Acquired*</Label>
                    <Input value={formData.poolBalance} onChange={(e) => handleInputChange('poolBalance', e.target.value)} onBlur={(e) => setFormData((p: any) => ({ ...p, poolBalance: fmtAmount(e.target.value) }))} className={errors.poolBalance ? 'error' : ''} disabled={isFieldDisabled('poolBalance')} />
                    {errors.poolBalance && <FieldError>{errors.poolBalance}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Purchase Price (% of par)*</Label>
                    <Input type="number" step="0.01" value={formData.price} onChange={(e) => handleInputChange('price', e.target.value)} className={errors.price ? 'error' : ''} disabled={isFieldDisabled('price')} />
                    {errors.price && <FieldError>{errors.price}</FieldError>}
                  </FormGroup>
                </FormRow>
              </StructuredBand>

              <EconomicsPreview
                toneName="rml"
                title="Live Pool Economics"
                notionalLabel="Pool UPB"
                notionalValue={poolBalance}
                price={price}
                consideration={consideration}
                currency={formData.currency || 'USD'}
                extraMetrics={[
                  { label: 'WAC', value: formData.wacPct ? `${formData.wacPct}%` : '-' },
                  { label: 'WA FICO', value: formData.waFico || '-' },
                  { label: '3M CPR', value: formData.cpr3mPct ? `${formData.cpr3mPct}%` : '-' },
                  { label: 'CDR / Severity', value: `${formData.cdrPct || '-'} / ${formData.severityPct || '-'}` },
                ]}
              />
            </TradeEconomicsGrid>

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="rml"><ListChecks size={14} /> Pool Definition</BandEyebrow>
                  <BandTitle>Collateral Identity</BandTitle>
                  <BandSubline>Pool type, agency, lien, counterparties, product, and maturity profile.</BandSubline>
                </BandTitleGroup>
                <ScopePill $tone="rml">Whole Loans</ScopePill>
              </BandHeader>

              <FormRow>
                <FormGroup style={{ flex: 2 }}>
                  <Label>Pool Name*</Label>
                  <Input value={formData.poolName} placeholder="Verus Non-QM Forward Flow 2025-Q2" onChange={(e) => handleInputChange('poolName', e.target.value)} className={errors.poolName ? 'error' : ''} disabled={isFieldDisabled('poolName')} />
                  {errors.poolName && <FieldError>{errors.poolName}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label>Pool Type</Label>
                  <Select value={formData.poolType} onChange={(e) => handleInputChange('poolType', e.target.value)} disabled={isFieldDisabled('poolType')}>
                    <option value="PRIME_JUMBO">Prime Jumbo</option>
                    <option value="NON_QM">Non-QM</option>
                    <option value="RPL">Re-Performing (RPL)</option>
                    <option value="NPL">Non-Performing (NPL)</option>
                    <option value="SECOND_LIEN">Second Lien</option>
                    <option value="HELOC">HELOC</option>
                    <option value="AGENCY_ELIGIBLE">Agency-Eligible</option>
                    <option value="INVESTOR_DSCR">Investor DSCR</option>
                    <option value="SCRATCH_DENT">Scratch & Dent</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Lien</Label>
                  <Select value={formData.lienPosition} onChange={(e) => handleInputChange('lienPosition', e.target.value)} disabled={isFieldDisabled('lienPosition')}>
                    <option value="FIRST">First</option>
                    <option value="SECOND">Second</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Agency</Label>
                  <Select value={formData.agency} onChange={(e) => handleInputChange('agency', e.target.value)} disabled={isFieldDisabled('agency')}>
                    <option value="NON_AGENCY">Non-Agency</option>
                    <option value="FNMA">Fannie Mae</option>
                    <option value="FHLMC">Freddie Mac</option>
                    <option value="GNMA">Ginnie Mae</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Product</Label>
                  <Select value={formData.productType} onChange={(e) => handleInputChange('productType', e.target.value)} disabled={isFieldDisabled('productType')}>
                    <option value="FRM_30">30Y Fixed</option>
                    <option value="FRM_15">15Y Fixed</option>
                    <option value="ARM">ARM</option>
                    <option value="HYBRID_ARM">Hybrid ARM</option>
                    <option value="IO">Interest-Only</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Originator / Seller</Label>
                  <Input value={formData.originatorName} onChange={(e) => handleInputChange('originatorName', e.target.value)} disabled={isFieldDisabled('originatorName')} />
                </FormGroup>
                <FormGroup>
                  <Label>Servicer</Label>
                  <Input value={formData.servicerName} onChange={(e) => handleInputChange('servicerName', e.target.value)} disabled={isFieldDisabled('servicerName')} />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>WA Maturity</Label>
                  <DateField value={formData.waMaturityDate ?? ''} disabled={isFieldDisabled('waMaturityDate')} onChange={iso => handleInputChange('waMaturityDate', iso)} />
                </FormGroup>
              </FormRow>
            </StructuredBand>

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="rml"><TrendingDown size={14} /> Collateral Stats</BandEyebrow>
                  <BandTitle>Credit And Prepayment Inputs</BandTitle>
                  <BandSubline>Weighted-average borrower quality, leverage, prepayment, default, severity, and delinquency metrics.</BandSubline>
                </BandTitleGroup>
              </BandHeader>

              <FormRow>
                <FormGroup>
                  <Label># Loans</Label>
                  <Input type="number" value={formData.numLoans} onChange={(e) => handleInputChange('numLoans', e.target.value)} disabled={isFieldDisabled('numLoans')} />
                </FormGroup>
                <FormGroup>
                  <Label>WA FICO</Label>
                  <Input type="number" value={formData.waFico} onChange={(e) => handleInputChange('waFico', e.target.value)} disabled={isFieldDisabled('waFico')} />
                </FormGroup>
                <FormGroup>
                  <Label>WA LTV (%)</Label>
                  <Input type="number" step="0.1" value={formData.waLtvPct} onChange={(e) => handleInputChange('waLtvPct', e.target.value)} disabled={isFieldDisabled('waLtvPct')} />
                </FormGroup>
                <FormGroup>
                  <Label>WA DTI (%)</Label>
                  <Input type="number" step="0.1" value={formData.waDtiPct} onChange={(e) => handleInputChange('waDtiPct', e.target.value)} disabled={isFieldDisabled('waDtiPct')} />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>WAC (%)</Label>
                  <Input type="number" step="0.01" value={formData.wacPct} onChange={(e) => handleInputChange('wacPct', e.target.value)} disabled={isFieldDisabled('wacPct')} />
                </FormGroup>
                <FormGroup>
                  <Label>3M CPR (%)</Label>
                  <Input type="number" step="0.1" value={formData.cpr3mPct} onChange={(e) => handleInputChange('cpr3mPct', e.target.value)} disabled={isFieldDisabled('cpr3mPct')} />
                </FormGroup>
                <FormGroup>
                  <Label>CDR (%)</Label>
                  <Input type="number" step="0.01" value={formData.cdrPct} onChange={(e) => handleInputChange('cdrPct', e.target.value)} disabled={isFieldDisabled('cdrPct')} />
                </FormGroup>
                <FormGroup>
                  <Label>Severity (%)</Label>
                  <Input type="number" step="0.1" value={formData.severityPct} onChange={(e) => handleInputChange('severityPct', e.target.value)} disabled={isFieldDisabled('severityPct')} />
                </FormGroup>
                <FormGroup>
                  <Label>90+ DQ (%)</Label>
                  <Input type="number" step="0.01" value={formData.delinquency90PlusPct} onChange={(e) => handleInputChange('delinquency90PlusPct', e.target.value)} disabled={isFieldDisabled('delinquency90PlusPct')} />
                </FormGroup>
              </FormRow>
            </StructuredBand>
          </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default RMLTicketScreen;
